"use client";

import { useCallback, useEffect, useState } from "react";
import { useAuth } from "@/providers/auth-provider";
import {
  KW_PANEL_FLAG_KEY,
  isTopinitEmail,
  kwPanelVisible,
  readKwPanelFlag,
  toggleKwPanelFlag,
} from "@/lib/kw-panel-flag";

/** In-tab broadcast so the sidebar switch and the panel agree without a shared provider. */
const CHANGE_EVENT = "kw-panel-flag-change";

/**
 * Same storage-plus-event shape as use-pro-mode.ts, but read after mount so the
 * server and the first client render agree on "off" and the badge hydrates clean.
 */
export function useKwPanelFlag() {
  const { user } = useAuth();
  const canToggle = isTopinitEmail(user?.email);
  const [on, setOn] = useState(false);
  /** False until the mount effect has read storage; a gate must not act before then. */
  const [ready, setReady] = useState(false);

  useEffect(() => {
    setOn(readKwPanelFlag(window.localStorage));
    setReady(true);
    const onStorage = (e: StorageEvent) => {
      if (e.key === KW_PANEL_FLAG_KEY) setOn(e.newValue === "1");
    };
    const onLocal = (e: Event) => setOn((e as CustomEvent<boolean>).detail === true);
    window.addEventListener("storage", onStorage);
    window.addEventListener(CHANGE_EVENT, onLocal);
    return () => {
      window.removeEventListener("storage", onStorage);
      window.removeEventListener(CHANGE_EVENT, onLocal);
    };
  }, []);

  const toggle = useCallback(() => {
    const next = toggleKwPanelFlag(window.localStorage);
    setOn(next);
    window.dispatchEvent(new CustomEvent(CHANGE_EVENT, { detail: next }));
  }, []);

  return { canToggle, on, ready, visible: kwPanelVisible(user?.email, on), toggle };
}
